import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '@assets/css/common/style_main.css';
import '@assets/css/common/style_table.css';
import '@assets/css/common/style_modal.css';
import '@assets/css/common/style_calendar.css';
import Calendar from '@assets/js/common/calendar.jsx';
import PagePresetInputModal from '@pages/page-preset-management/components/PagePresetInputModal.jsx';
import PresetTable from '@pages/page-preset-management/components/PresetTable.jsx';
import PresetDetailModal from '@pages/page-preset-management/components/PresetDetailModal.jsx';
import PaginationZeroBased from '@components/common/PaginationZeroBased';
import moment from 'moment';

// API Services
import presetApi from '@services/presetApi.jsx';
import themeApi from '@services/themeApi.jsx';
import branchApi from '@services/branchApi.jsx';

// Custom hooks
import useAuth from '@hooks/useAuth.jsx';

const PagePresetManagement = () => {
  const navigate = useNavigate();
  
  // useAuth 훅 사용
  const { getUserFromToken } = useAuth();

  // JWT에서 직접 사용자 정보 가져오기
  const user = getUserFromToken();
  
  // 데이터 상태 관리
  const [presets, setPresets] = useState([]);
  const [themes, setThemes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // 체크박스 상태 관리
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // 모달 상태 관리
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [currentPreset, setCurrentPreset] = useState(null);
  
  // 모달 입력값 상태 관리
  const [modalInputs, setModalInputs] = useState({
    userName: user?.name || 'cineticmotion24',
    presetName: '',
    themeId: '',
    branchId: '',
    startDatetime: null,
    endDatetime: null,
    isActive: false
  });
  
  // 달력 관련 상태 수정
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const [activeInput, setActiveInput] = useState(null);
  
  // 지점 관련 상태 관리
  const [selectedBranches, setSelectedBranches] = useState([]);
  
  // 상세정보 모달 상태 관리
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // 페이지네이션 상태 관리
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  
  // 프리셋 상세 정보(페이지 마스터 포함)를 가져오는 함수
  const fetchPresetWithPageMasters = async (presetId) => {
    try {
      const response = await presetApi.getPreset(presetId);
      return response || null;
    } catch (err) {
      console.error('Error fetching preset with page masters:', err);
      return null;
    }
  };

  // 서브 페이지로 이동하는 함수 (프리셋 + 프리셋 페이지 마스터 정보 함께 전달)
  const navigateToPresetPage = async (preset, additionalData = {}) => {
    try {
      // 상세 프리셋 정보(페이지 마스터 포함) 가져오기
      const detailedPreset = await fetchPresetWithPageMasters(preset.id);
      
      if (!detailedPreset) {
        setAlertMessage('프리셋 정보를 가져오는데 실패했습니다.');
        setShowAlertModal(true);
        return;
      }
      
      // 네비게이션 데이터 구성
      const navigationData = {
        preset: {
          id: detailedPreset.id,
          presetName: detailedPreset.presetName,
          themeName: detailedPreset.themeName,
          themeId: detailedPreset.themeId,
          branchId: detailedPreset.branchId,
          branchName: detailedPreset.branchName,
          startDatetime: detailedPreset.startDatetime,
          endDatetime: detailedPreset.endDatetime,
          isActive: detailedPreset.isActive,
          createdByName: detailedPreset.createdByName
        },
        presetPageMasters: detailedPreset.presetPageMasters || [], // API 응답에 포함되어 있다고 가정
        ...additionalData
      };
      
      // URL 파라미터와 함께 서브 페이지로 이동
      const searchParams = new URLSearchParams({
        presetId: detailedPreset.id.toString(),
        page: additionalData.page || 'start',
        theme: detailedPreset.presetName || '',
        author: detailedPreset.createdByName || '',
        date: detailedPreset.startDatetime || ''
      });
      
      navigate(`/page-preset-management/page-preset?${searchParams.toString()}`, {
        state: navigationData
      });
    } catch (error) {
      console.error('Error navigating to preset page:', error);
      setAlertMessage('페이지 이동 중 오류가 발생했습니다.');
      setShowAlertModal(true);
    }
  };
  const fetchPresets = async (page = 0, size = 20) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await presetApi.getPresets(page, size);
      setPresets(response.content || []);
    } catch (err) {
      setError('프리셋 목록을 불러오는데 실패했습니다.');
      console.error('Error fetching presets:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchThemes = async () => {
    try {
      const themesData = await themeApi.getAllThemes();
      setThemes(themesData || []);
    } catch (err) {
      console.error('Error fetching themes:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const branchesData = await branchApi.getBranches();
      setBranches(branchesData || []);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchPresets();
    fetchThemes();
    fetchBranches();
  }, []);
  
  // 체크박스 전체 선택/해제 핸들러
  const handleSelectAll = (e) => {
    setSelectAll(e.target.checked);
    // 모든 프리셋 ID 또는 빈 배열로 설정
    setSelectedItems(e.target.checked ? presets.map(preset => preset.id) : []);
  };
  
  // 개별 체크박스 핸들러
  const handleItemSelect = (id, e) => {
    if (e.target.checked) {
      setSelectedItems([...selectedItems, id]);
    } else {
      setSelectedItems(selectedItems.filter(item => item !== id));
    }
    
    // 전체 선택 상태 업데이트
    if (e.target.checked && selectedItems.length + 1 === presets.length) {
      setSelectAll(true);
    } else if (!e.target.checked && selectAll) {
      setSelectAll(false);
    }
  };
  
  // 프리셋 추가 버튼 핸들러
  const handleAddPresetClick = () => {
    // 입력값 초기화
    setModalInputs({
      userName: user?.name || 'cineticmotion24',
      presetName: '',
      themeId: '',
      branchId: '',
      startDatetime: null,
      endDatetime: null,
      isActive: false
    });
    
    // 지점 초기화
    setSelectedBranches([]);
    
    setShowAddModal(true);
  };
  
  // 편집 버튼 핸들러
  const handleEditClick = (preset) => {
    setCurrentPreset(preset);
    
    setModalInputs({
      userName: preset?.createdByName || user?.name || 'cineticmotion24',
      presetName: preset.presetName,
      themeId: preset.themeId,
      branchId: preset.branchId,
      startDatetime: moment(preset.startDatetime).format("YYYYMMDD"),
      endDatetime: moment(preset.endDatetime).format("YYYYMMDD"),
      isActive: preset.isActive || false
    });
    
    // 지점 초기화
    setSelectedBranches(preset.branchName ? [{ id: preset.branchId, name: preset.branchName }] : []);
    
    setShowEditModal(true);
  };
  
  // 삭제 버튼 핸들러
  const handleDeleteClick = (preset) => {
    setCurrentPreset(preset);
    setConfirmMessage(`${preset.presetName} 프리셋을 삭제하시겠습니까?`);
    setConfirmCallback(() => async () => {
      try {
        // 프리셋 삭제 API 호출
        await presetApi.deletePreset(preset.id);
        
        // 로컬 상태 업데이트
        setPresets(prevPresets => prevPresets.filter(p => p.id !== preset.id));
        
        setAlertMessage('삭제되었습니다.');
        setShowAlertModal(true);
      } catch (error) {
        setAlertMessage('삭제 중 오류가 발생했습니다.');
        setShowAlertModal(true);
      }
    });
    setShowConfirmModal(true);
  };
  
  // 모달 입력값 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setModalInputs(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // 모달 닫기 핸들러
  const handleCloseModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowConfirmModal(false);
    setShowAlertModal(false);
    setShowCalendar(false);
  };
  
  // 알림 모달만 닫는 함수
  const handleCloseAlertModal = () => {
    setShowAlertModal(false);
  };
  
  // 확인 모달만 닫는 함수
  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
  };
  
  // 중복 확인 핸들러
  const handleCheckDuplicate = () => {
    const presetName = modalInputs.presetName.trim();
    if (!presetName) {
      setAlertMessage('프리셋명을 입력해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    // 기존 프리셋명 목록
    const existingPresets = presets.map(preset => preset.presetName);
    
    // 수정 모드에서는 현재 편집 중인 프리셋명은 제외
    if (showEditModal && currentPreset && presetName === currentPreset.presetName) {
      setAlertMessage('사용 가능한 프리셋명입니다.');
      setShowAlertModal(true);
      return;
    }
    
    if (existingPresets.includes(presetName)) {
      setAlertMessage('이미 사용 중인 프리셋명입니다. 다른 프리셋명을 입력해주세요.');
      setShowAlertModal(true);
    } else {
      setAlertMessage('사용 가능한 프리셋명입니다.');
      setShowAlertModal(true);
    }
  };
  
  // 프리셋 추가/수정 확인 핸들러
  const handleConfirm = async () => {
    // 입력값 검증
    if (!modalInputs.presetName) {
      setAlertMessage('프리셋명을 입력해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    if (!modalInputs.themeId) {
      setAlertMessage('디자인 테마를 선택해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    if (!selectedBranches.length) {
      setAlertMessage('지점을 선택해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    if (!modalInputs.startDatetime || !modalInputs.endDatetime) {
      setAlertMessage('사용 기간을 선택해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    try {
      if (showAddModal) {
        // 프리셋 추가 API 호출
        const responseData = await presetApi.createPreset({
          presetName: modalInputs.presetName,
          themeId: parseInt(modalInputs.themeId),
          branchId: selectedBranches[0].id, // 첫 번째 지점 사용
          startDatetime: modalInputs.startDatetime,
          endDatetime: modalInputs.endDatetime,
          isActive: modalInputs.isActive,
        });

        // 로컬 상태 업데이트
        setPresets(prevPresets => [responseData, ...prevPresets]);

        setShowAddModal(false);

        // 확인 모달 표시
        setConfirmMessage('프리셋을 진행하시겠습니까?');
        setConfirmCallback(() => async () => {
          // PresetPageMaster 정보와 함께 페이지 이동
          await navigateToPresetPage(responseData, {
            page: 'start',
            mode: 'create'
          });
        });
        setShowConfirmModal(true);
      } else if (showEditModal && currentPreset) {
        // 프리셋 수정 API 호출
        const updatedData = await presetApi.updatePreset(currentPreset.id, {
          presetName: modalInputs.presetName,
          themeId: parseInt(modalInputs.themeId),
          branchId: selectedBranches[0]?.id || currentPreset.branchId,
          startDatetime: modalInputs.startDatetime,
          endDatetime: modalInputs.endDatetime,
          isActive: modalInputs.isActive,
        });

        // 로컬 상태 업데이트
        setPresets(prevPresets => 
          prevPresets.map(preset => 
            preset.id === currentPreset.id ? updatedData : preset
          )
        );

        setAlertMessage('프리셋 정보가 수정되었습니다.');
        setShowEditModal(false);
        setShowAlertModal(true);
      }
    } catch (error) {
      console.error(error);
      setAlertMessage('처리 중 오류가 발생했습니다.');
      setShowAlertModal(true);
    }
  };
  
  // 프리셋 수정 페이지로 이동 핸들러
  const handlePresetEditClick = async () => {
    if (!currentPreset) return;
    
    // 변경사항이 있는지 확인
    const hasChanges = modalInputs.presetName !== currentPreset.presetName ||
                      modalInputs.themeId !== currentPreset.themeId ||
                      modalInputs.startDatetime !== moment(currentPreset.startDatetime).format('YYYYMMDD') ||
                      modalInputs.endDatetime !== moment(currentPreset.endDatetime).format('YYYYMMDD') ||
                      modalInputs.isActive !== currentPreset.isActive;
    
    if (hasChanges) {
      setConfirmMessage('현재 프리셋의 변경사항이 있습니다. 저장하시겠습니까?');
      setConfirmCallback(() => async () => {
        try {
          // 변경사항 저장
          await handleConfirm();
          
          // 최신 프리셋 정보로 페이지 이동
          const updatedPreset = {
            ...currentPreset,
            presetName: modalInputs.presetName,
            themeId: modalInputs.themeId,
            startDatetime: modalInputs.startDatetime,
            endDatetime: modalInputs.endDatetime,
            isActive: modalInputs.isActive
          };
          
          await navigateToPresetPage(updatedPreset, {
            page: 'start',
            mode: 'edit'
          });
        } catch (error) {
          console.error('Error saving and navigating:', error);
        }
      });
      setShowConfirmModal(true);
    } else {
      // 변경사항이 없으면 바로 이동
      await navigateToPresetPage(currentPreset, {
        page: 'start',
        mode: 'edit'
      });
    }
  };
  
  // 확인 모달 확인 버튼 핸들러
  const handleConfirmModalConfirm = () => {
    if (confirmCallback) {
      confirmCallback();
    }
    setShowConfirmModal(false);
  };
  
  // 달력 선택 핸들러 수정
  const handleDateClick = (dateType, event) => {
    const input = event.target;
    const rect = input.getBoundingClientRect();
    
    setCalendarPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left
    });
    
    setActiveInput(dateType);
    setShowCalendar(true);
  };

  // 날짜 선택 핸들러 수정
  const handleDateSelect = (startDatetime, endDatetime) => {
    // 시작일과 종료일을 함께 업데이트
    setModalInputs(prev => ({
      ...prev,
      startDatetime: moment(startDatetime).format("YYYYMMDD"),
      endDatetime: moment(endDatetime).format("YYYYMMDD")
    }));
    setShowCalendar(false);
  };
  
  // 일괄 삭제 핸들러
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    
    setConfirmMessage(`선택한 ${selectedItems.length}개의 프리셋을 삭제하시겠습니까?`);
    setConfirmCallback(() => async () => {
      try {
        // 선택된 모든 프리셋 삭제
        const deletePromises = selectedItems.map((id) => presetApi.deletePreset(id));
        await Promise.all(deletePromises);
        
        // 로컬 상태 업데이트
        setPresets(prevPresets => prevPresets.filter(preset => !selectedItems.includes(preset.id)));
        
        setSelectedItems([]);
        setSelectAll(false);
        setAlertMessage('선택한 프리셋이 삭제되었습니다.');
        setShowAlertModal(true);
      } catch (error) {
        setAlertMessage('삭제 중 오류가 발생했습니다.');
        setShowAlertModal(true);
      }
    });
    setShowConfirmModal(true);
  };
  
  // 지점 추가 핸들러
  const handleAddBranch = (branchesToAdd) => {
    if (!branchesToAdd || branchesToAdd.length === 0) return;
    
    // 다중 지점 추가
    const newBranches = branchesToAdd.filter(branch => 
      !selectedBranches.some(b => b.id === branch.id)
    );
    
    if (newBranches.length > 0) {
      setSelectedBranches([...selectedBranches, ...newBranches]);
    }
  };
  
  // 지점 제거 핸들러
  const handleRemoveBranch = (branchIds) => {
    if (!branchIds || branchIds.length === 0) return;
    
    // 다중 지점 제거
    setSelectedBranches(selectedBranches.filter(b => !branchIds.includes(b.id)));
  };
  
  // 상세정보 모달 닫기 핸들러
  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
  };
  
  // 상세정보 아이콘 클릭 핸들러
  const handleInfoClick = (preset) => {
    setCurrentPreset(preset);
    setShowDetailModal(true);
  };
  
  // 페이지 변경 핸들러
  const handlePageChange = (newPage) => {
    setPage(newPage);
    // 실제 구현에서는 여기서 fetchPresets(newPage) 호출
  };
  
  return (
    <>
      <div className="page-title-container">
        <h1 className="page-title">페이지 프리셋</h1>
        <div className="page-title-icon">
          <a href="#" id="add-theme-btn" title="페이지 프리셋 추가" onClick={handleAddPresetClick}>
            <i className="ri-add-circle-line"></i>
          </a>
        </div>
      </div>
      
      <div className="page-container">
        
        {/* 로딩 표시 */}
        {loading && <div className="loading">로딩 중...</div>}

        {/* 에러 표시 */}
        {error && <div className="error-message">{error}</div>}
        
        {/* 필터 드롭다운 */}
        <div className="filter-dropdown-container-row">
          {/* 일괄 삭제 버튼 */}
          <div className={`bulk-delete-container ${selectedItems.length >= 2 ? 'active' : ''}`}>
            <button className="bulk-delete-btn" onClick={handleBulkDelete}>
              일괄 삭제 ({selectedItems.length})
            </button>
          </div>

          <div className="filter-dropdown">
            <select id="theme-filter">
              <option value="latest">최신순</option>
              <option value="name">이름순</option>
              <option value="period">사용기간순</option>
            </select>
          </div>
        </div>
        
        {/* 프리셋 테이블 */}
        <PresetTable
          presets={presets}
          selectAll={selectAll}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onItemSelect={handleItemSelect}
          onInfoClick={handleInfoClick}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
        />
        
        {/* 페이지네이션 */}
        <PaginationZeroBased
          page={page}
          totalPages={totalPages}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          onPageChange={handlePageChange}
        />
      </div>
      
      {/* 프리셋 추가 모달 */}
      {showAddModal && (
        <PagePresetInputModal
          title="페이지 프리셋 추가"
          inputs={modalInputs}
          setInputs={setModalInputs}
          onClose={handleCloseModal}
          onConfirm={handleConfirm}
          onCheckDuplicate={handleCheckDuplicate}
          availableBranches={branches || []}
          selectedBranches={selectedBranches}
          onAddBranch={handleAddBranch}
          onRemoveBranch={handleRemoveBranch}
          showToggle={true}
          toggleLabel="배포 여부"
          toggleName="isActive"
          showSettingButton={false}
          itemNameLabel="프리셋명"
          itemNamePlaceholder="프리셋명을 입력하세요"
          itemNameField="presetName"
          isEdit={false}
          themes={themes}
        />
      )}
      
      {/* 프리셋 수정 모달 */}
      {showEditModal && currentPreset && (
        <PagePresetInputModal
          title="페이지 프리셋 수정"
          inputs={modalInputs}
          setInputs={setModalInputs}
          onClose={handleCloseModal}
          onConfirm={handleConfirm}
          onCheckDuplicate={handleCheckDuplicate}
          availableBranches={branches || []}
          selectedBranches={selectedBranches}
          onAddBranch={handleAddBranch}
          onRemoveBranch={handleRemoveBranch}
          showToggle={true}
          toggleLabel="배포 여부"
          toggleName="isActive"
          showSettingButton={true}
          settingButtonText="페이지 프리셋"
          onSettingClick={handlePresetEditClick}
          itemNameLabel="프리셋명"
          itemNamePlaceholder="프리셋명을 입력하세요"
          itemNameField="presetName"
          isEdit={true}
          themes={themes}
        />
      )}
      
      {/* 알림 모달 */}
      {showAlertModal && (
        <div className="modal-overlay active" id="alert-modal">
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">알림</h3>
              <button className="modal-close" onClick={handleCloseAlertModal}>&times;</button>
            </div>
            <div className="modal-body">
              <p>{alertMessage}</p>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn modal-btn-primary"
                onClick={handleCloseAlertModal}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 확인 모달 */}
      {showConfirmModal && (
        <div className="modal-overlay active" id="confirm-modal">
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">확인</h3>
              <button className="modal-close" onClick={handleCloseConfirmModal}>&times;</button>
            </div>
            <div className="modal-body">
              <p>{confirmMessage}</p>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn modal-btn-cancel"
                onClick={handleCloseConfirmModal}
              >
                아니오
              </button>
              <button 
                className="modal-btn modal-btn-primary"
                onClick={handleConfirmModalConfirm}
              >
                예
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 달력 컴포넌트 추가 */}
      {showCalendar && (
        <Calendar
          onDateSelect={handleDateSelect}
          onClose={() => setShowCalendar(false)}
          position={calendarPosition}
        />
      )}

      {/* 상세정보 모달 */}
      <PresetDetailModal
        isOpen={showDetailModal}
        preset={currentPreset}
        onClose={handleCloseDetailModal}
      />
    </>
  );
};

export default PagePresetManagement; 